import React, { useState, useEffect, useCallback } from 'react'
import {
  DollarSign, TrendingDown, RefreshCw, Copy, Check, Filter,
  HardDrive, Network, Cpu, Clock, AlertTriangle, Layers,
  ExternalLink, Search, CheckCircle2, Info, ArrowUpRight
} from 'lucide-react'
import { getClients, getCostGuardScan, getCostGuardHistory } from '../api'

const CATEGORY_META = {
  unattached_ebs: {
    label: 'Unattached EBS Volume',
    bg: '#FEF9E7',
    color: '#92400E',
    border: '#D97706',
    icon: HardDrive,
  },
  idle_nat_gateway: {
    label: 'Idle NAT Gateway',
    bg: '#FDF2F0',
    color: '#991B1B',
    border: '#EA580C',
    icon: Network,
  },
  idle_eip: {
    label: 'Idle Elastic IP',
    bg: '#FEF3C7',
    color: '#B45309',
    border: '#D97706',
    icon: Network,
  },
  stale_snapshot: {
    label: 'Stale Snapshot (>90d)',
    bg: '#E0F2FE',
    color: '#0369A1',
    border: '#0284C7',
    icon: Clock,
  },
  graviton_opportunity: {
    label: 'Graviton Modernization',
    bg: '#ECFDF5',
    color: '#065F46',
    border: '#10B981',
    icon: Cpu,
  },
}

export default function CloudIQCostGuard() {
  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState('')
  const [activeTab, setActiveTab] = useState('findings') // 'findings' | 'history'

  // Data states
  const [scanData, setScanData] = useState(null)
  const [historyData, setHistoryData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Filter states
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [copiedId, setCopiedId] = useState(null)

  const loadClients = useCallback(async () => {
    try {
      const res = await getClients()
      const clientList = Array.isArray(res?.clients) ? res.clients : []
      setClients(clientList)
      if (clientList.length > 0 && !selectedClient) {
        setSelectedClient(clientList[0].id)
      }
    } catch {
      setClients([])
    }
  }, [selectedClient])

  useEffect(() => {
    loadClients()
  }, [loadClients])

  const fetchCostData = useCallback(async () => {
    if (!selectedClient) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const [scan, hist] = await Promise.all([
        getCostGuardScan(selectedClient),
        getCostGuardHistory(selectedClient, 25)
      ])
      setScanData(scan)
      setHistoryData(hist)
    } catch {
      setScanData(null)
      setHistoryData(null)
    } finally {
      setLoading(false)
    }
  }, [selectedClient])

  useEffect(() => {
    fetchCostData()
  }, [fetchCostData])

  const handleRefresh = async () => {
    if (!selectedClient) return
    setRefreshing(true)
    try {
      await fetchCostData()
    } finally {
      setRefreshing(false)
    }
  }

  const handleCopyCommand = (command, id) => {
    if (!command) return
    navigator.clipboard.writeText(command)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const rawFindings = Array.isArray(scanData?.findings) ? scanData.findings : []
  const filteredFindings = rawFindings.filter((item) => {
    if (categoryFilter !== 'all' && item.category !== categoryFilter) return false
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    return (
      (item.resource_id && item.resource_id.toLowerCase().includes(q)) ||
      (item.region && item.region.toLowerCase().includes(q)) ||
      (item.remediation_guidance && item.remediation_guidance.toLowerCase().includes(q))
    )
  })

  const formatCurrency = (val) => {
    const num = Number(val)
    if (isNaN(num)) return '0.00'
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const rawSummary = scanData?.summary || {}
  const summary = {
    total_monthly_waste: Number(rawSummary.total_monthly_waste || 0),
    total_annual_waste: Number(rawSummary.total_annual_waste || 0),
    unattached_ebs_monthly_waste: Number(rawSummary.unattached_ebs_monthly_waste || 0),
    idle_nat_gateways_monthly_waste: Number(rawSummary.idle_nat_gateways_monthly_waste ?? rawSummary.idle_nat_monthly_waste ?? 0),
    idle_eips_monthly_waste: Number(rawSummary.idle_eips_monthly_waste || 0),
    stale_snapshots_monthly_waste: Number(rawSummary.stale_snapshots_monthly_waste || 0),
    graviton_potential_monthly_savings: Number(rawSummary.graviton_potential_monthly_savings ?? rawSummary.graviton_savings_potential ?? 0),
    findings_count: Number(rawSummary.findings_count ?? rawFindings.length ?? 0),
    unattached_ebs_count: Number(rawSummary.unattached_ebs_count || 0),
    idle_nat_gateways_count: Number(rawSummary.idle_nat_gateways_count || 0),
    idle_eips_count: Number(rawSummary.idle_eips_count || 0),
    stale_snapshots_count: Number(rawSummary.stale_snapshots_count || 0),
    graviton_candidates_count: Number(rawSummary.graviton_candidates_count ?? rawFindings.filter((f) => f.category === 'GRAVITON_RIGHTSIZING').length ?? 0),
  }

  return (
    <div className="animate-in" style={{ maxWidth: 1280, margin: '0 auto' }}>
      {/* Header Banner */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              background: '#ECFDF5',
              padding: 7,
              borderRadius: 8,
              border: '1.5px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#059669'
            }}>
              <DollarSign size={20} />
            </div>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 800, margin: 0, color: 'var(--ink-900)' }}>
              CloudIQ CostGuard &mdash; FinOps Waste Engine
            </h2>
          </div>
          <p style={{ margin: '4px 0 0', color: 'var(--ink-600)', fontSize: 13 }}>
            Continuous FinOps scanning for unattached EBS volumes, idle NAT gateways, unassigned Elastic IPs, and Graviton migration savings.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="input-select"
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: '1.5px solid var(--border-ink)',
              fontSize: 13,
              fontWeight: 600,
              background: '#FFFFFF',
              boxShadow: 'var(--shadow-solid-sm)',
              color: 'var(--ink-900)'
            }}
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.client_name || c.id}</option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="btn btn-secondary btn-sm"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              border: '1.5px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              borderRadius: 8,
              fontWeight: 700
            }}
          >
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Scanning...' : 'Refresh Scan'}
          </button>
        </div>
      </div>

      {/* Unconfigured / Missing Credentials Banner */}
      {scanData && !scanData.available && (
        <div style={{
          background: 'var(--brand-yellow-bg)',
          border: '2px solid var(--border-ink)',
          borderRadius: 12,
          padding: '16px 20px',
          boxShadow: 'var(--shadow-solid-sm)',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12
        }}>
          <AlertTriangle size={20} style={{ color: 'var(--brand-coral)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--ink-900)' }}>
              {scanData.reason_code === 'credentials_missing'
                ? 'AWS Access Credentials Not Configured'
                : 'Cost Scan Unavailable'}
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-700)' }}>
              {scanData.reason_message ||
                'CloudIQ CostGuard requires read-only AWS credentials (STS cross-account role or IAM keys) to inspect EBS volumes, Elastic IPs, NAT Gateways, and CloudWatch metrics. Please configure credentials in Client Settings.'}
            </p>
          </div>
        </div>
      )}

      {/* KPI Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 16,
        marginBottom: 24
      }}>
        {/* Identified Monthly Waste */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid var(--brand-coral)',
          padding: '16px 20px',
          boxShadow: 'var(--shadow-solid-sm)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-600)' }}>
              Identified Monthly Waste
            </span>
            <div style={{
              background: 'var(--brand-coral-bg)',
              padding: 5,
              borderRadius: 6,
              border: '1.5px solid var(--border-ink)',
              color: 'var(--brand-coral)'
            }}>
              <TrendingDown size={14} />
            </div>
          </div>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: 'var(--brand-coral)' }}>
              ${formatCurrency(summary.total_monthly_waste)}
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-500)', marginLeft: 4 }}>/mo</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
              ${formatCurrency(summary.total_annual_waste)} /year projected
            </div>
          </div>
        </div>

        {/* Unattached EBS */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid #D97706',
          padding: '16px 20px',
          boxShadow: 'var(--shadow-solid-sm)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-600)' }}>
              Unattached EBS
            </span>
            <div style={{
              background: '#FEF3C7',
              padding: 5,
              borderRadius: 6,
              border: '1.5px solid var(--border-ink)',
              color: '#B45309'
            }}>
              <HardDrive size={14} />
            </div>
          </div>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: 'var(--ink-900)' }}>
              ${formatCurrency(summary.unattached_ebs_monthly_waste)}
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-500)', marginLeft: 4 }}>/mo</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 4 }}>
              {summary.unattached_ebs_count} detached storage volume{summary.unattached_ebs_count === 1 ? '' : 's'}
            </div>
          </div>
        </div>

        {/* Zombie Network Spend */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid var(--brand-teal)',
          padding: '16px 20px',
          boxShadow: 'var(--shadow-solid-sm)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-600)' }}>
              Zombie Network Spend
            </span>
            <div style={{
              background: 'var(--brand-teal-bg)',
              padding: 5,
              borderRadius: 6,
              border: '1.5px solid var(--border-ink)',
              color: 'var(--brand-teal)'
            }}>
              <Network size={14} />
            </div>
          </div>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: 'var(--ink-900)' }}>
              ${formatCurrency(summary.idle_nat_gateways_monthly_waste + summary.idle_eips_monthly_waste)}
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-500)', marginLeft: 4 }}>/mo</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 4 }}>
              {summary.idle_nat_gateways_count} idle NAT GWs &middot; {summary.idle_eips_count} unassigned EIPs
            </div>
          </div>
        </div>

        {/* Graviton Modernization */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid #10B981',
          padding: '16px 20px',
          boxShadow: 'var(--shadow-solid-sm)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-600)' }}>
              Graviton Modernization
            </span>
            <div style={{
              background: '#ECFDF5',
              padding: 5,
              borderRadius: 6,
              border: '1.5px solid var(--border-ink)',
              color: '#059669'
            }}>
              <Cpu size={14} />
            </div>
          </div>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: '#059669' }}>
              ${formatCurrency(summary.graviton_potential_monthly_savings)}
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-500)', marginLeft: 4 }}>/mo</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 4 }}>
              {summary.graviton_candidates_count} x86 instance{summary.graviton_candidates_count === 1 ? '' : 's'} eligible for ARM64
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Control matching Clients & Agents */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        borderBottom: '1.5px solid var(--border-ink)',
        paddingBottom: 10,
        flexWrap: 'wrap',
        gap: 12
      }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => setActiveTab('findings')}
            className={`btn ${activeTab === 'findings' ? 'btn-primary' : 'btn-secondary'}`}
            style={{
              padding: '8px 18px',
              fontSize: 13,
              border: '2px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              fontWeight: 700
            }}
          >
            <Layers size={14} style={{ marginRight: 6 }} />
            Active Waste Findings ({rawFindings.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`btn ${activeTab === 'history' ? 'btn-primary' : 'btn-secondary'}`}
            style={{
              padding: '8px 18px',
              fontSize: 13,
              border: '2px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              fontWeight: 700
            }}
          >
            <Clock size={14} style={{ marginRight: 6 }} />
            Historical Scan Trend
          </button>
        </div>

        {scanData?.scanned_at && (
          <span style={{ fontSize: 12, color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>
            Last scan: {new Date(scanData.scanned_at).toLocaleString()}
          </span>
        )}
      </div>

      {/* Tab 1: Findings Table */}
      {activeTab === 'findings' && (
        <div>
          {/* Controls: Search and Category filter */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            flexWrap: 'wrap'
          }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 260, maxWidth: 420 }}>
              <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-400)' }} />
              <input
                type="text"
                placeholder="Filter by resource ID, region, or guidance..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 34px',
                  borderRadius: 8,
                  border: '1.5px solid var(--border-ink)',
                  boxShadow: 'var(--shadow-solid-sm)',
                  fontSize: 13,
                  background: '#FFFFFF',
                  color: 'var(--ink-900)'
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Filter size={14} style={{ color: 'var(--ink-600)' }} />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{
                  padding: '7px 12px',
                  borderRadius: 8,
                  border: '1.5px solid var(--border-ink)',
                  boxShadow: 'var(--shadow-solid-sm)',
                  fontSize: 13,
                  fontWeight: 600,
                  background: '#FFFFFF',
                  color: 'var(--ink-900)'
                }}
              >
                <option value="all">All Categories</option>
                <option value="unattached_ebs">Unattached EBS Volumes</option>
                <option value="idle_nat_gateway">Idle NAT Gateways</option>
                <option value="idle_eip">Idle Elastic IPs</option>
                <option value="stale_snapshot">Stale Snapshots (&gt;90d)</option>
                <option value="graviton_opportunity">Graviton Opportunities</option>
              </select>
            </div>
          </div>

          {/* Findings List */}
          {loading ? (
            <div style={{
              background: '#FFFFFF',
              borderRadius: 12,
              border: '2px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              padding: '40px 20px',
              textAlign: 'center',
              color: 'var(--ink-500)',
              fontSize: 13
            }}>
              Scanning AWS resources for FinOps waste...
            </div>
          ) : filteredFindings.length === 0 ? (
            <div style={{
              background: '#FFFFFF',
              borderRadius: 12,
              border: '2px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              padding: '48px 24px',
              textAlign: 'center'
            }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#ECFDF5',
                border: '1.5px solid var(--border-ink)',
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px'
              }}>
                <CheckCircle2 size={24} />
              </div>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 800, margin: '0 0 6px', color: 'var(--ink-900)' }}>
                No Waste Detected
              </h3>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-600)', maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
                No idle NAT gateways, unattached EBS volumes, unused Elastic IPs, or stale snapshots found matching your criteria.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredFindings.map((finding) => {
                const meta = CATEGORY_META[finding.category] || {
                  label: finding.category,
                  bg: 'var(--canvas-subtle)',
                  color: 'var(--ink-700)',
                  border: 'var(--border-ink)',
                  icon: Info,
                }
                const CategoryIcon = meta.icon
                const isCopied = copiedId === finding.id

                return (
                  <div
                    key={finding.id}
                    style={{
                      background: '#FFFFFF',
                      borderRadius: 12,
                      border: '2px solid var(--border-ink)',
                      boxShadow: 'var(--shadow-solid-sm)',
                      padding: '16px 20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 16
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 280 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '3px 9px',
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 700,
                          background: meta.bg,
                          color: meta.color,
                          border: `1.5px solid ${meta.border}`
                        }}>
                          <CategoryIcon size={12} />
                          {meta.label}
                        </span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 800, color: 'var(--ink-900)' }}>
                          {finding.resource_id}
                        </span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)' }}>
                          [{finding.region}]
                        </span>
                      </div>

                      <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.5 }}>
                        {finding.remediation_guidance}
                      </p>

                      {/* Detail tags */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                        {finding.details?.size_gib && (
                          <span style={{ background: 'var(--canvas-subtle)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border-subtle)', color: 'var(--ink-700)' }}>
                            Size: {finding.details.size_gib} GiB ({finding.details.volume_type})
                          </span>
                        )}
                        {finding.details?.age_days && (
                          <span style={{ background: 'var(--canvas-subtle)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border-subtle)', color: 'var(--ink-700)' }}>
                            Age: {finding.details.age_days} days
                          </span>
                        )}
                        {finding.details?.bytes_in_7d !== undefined && (
                          <span style={{ background: 'var(--canvas-subtle)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border-subtle)', color: 'var(--ink-700)' }}>
                            7d Traffic: {(finding.details.bytes_in_7d / (1024 * 1024)).toFixed(2)} MB
                          </span>
                        )}
                        {finding.details?.current_instance_type && (
                          <span style={{ background: '#ECFDF5', padding: '2px 8px', borderRadius: 4, border: '1px solid #10B981', color: '#065F46', fontWeight: 700 }}>
                            {finding.details.current_instance_type} &rarr; {finding.details.graviton_instance_type}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Cost & Remediation Action */}
                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                      <div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 800, color: 'var(--brand-coral)' }}>
                          ${formatCurrency(finding.monthly_waste)}
                          <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--ink-500)' }}> /mo</span>
                        </div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)' }}>
                          ${formatCurrency(finding.annual_waste)} /year
                        </div>
                      </div>

                      {finding.safe_remediation_command && (
                        <button
                          type="button"
                          onClick={() => handleCopyCommand(finding.safe_remediation_command, finding.id)}
                          className="btn btn-secondary btn-sm"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                            border: '1.5px solid var(--border-ink)',
                            boxShadow: 'var(--shadow-solid-sm)',
                            borderRadius: 6,
                            fontSize: 11,
                            fontFamily: 'var(--font-mono)',
                            fontWeight: 700,
                            padding: '4px 10px'
                          }}
                          title="Copy safe AWS CLI command"
                        >
                          {isCopied ? (
                            <>
                              <Check size={12} color="#059669" />
                              <span style={{ color: '#059669' }}>Copied CLI</span>
                            </>
                          ) : (
                            <>
                              <Copy size={12} />
                              <span>Copy AWS CLI</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab 2: History & Trend */}
      {activeTab === 'history' && (
        <div style={{
          background: '#FFFFFF',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          boxShadow: 'var(--shadow-solid-md)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '2px solid var(--border-ink)',
            background: 'var(--canvas-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 15, fontWeight: 800, margin: 0, color: 'var(--ink-900)' }}>
                FinOps Waste Snapshots History
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-600)' }}>
                Chronological record of automated AWS scans, waste tracking, and realized cost reduction.
              </p>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: 'var(--ink-500)' }}>
              Loading history...
            </div>
          ) : !historyData?.snapshots || historyData.snapshots.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: 'var(--ink-500)' }}>
              No historical scans recorded for this tenant yet.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--canvas-subtle)', borderBottom: '2px solid var(--border-ink)' }}>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Timestamp</th>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Monthly Waste</th>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Annualized</th>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Unattached EBS</th>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Idle NAT GWs</th>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Idle EIPs</th>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Graviton Modernization</th>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Findings</th>
                  </tr>
                </thead>
                <tbody style={{ fontFamily: 'var(--font-mono)' }}>
                  {historyData.snapshots.map((row) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 16px', color: 'var(--ink-600)', fontFamily: 'var(--font-sans)' }}>
                        {new Date(row.scanned_at).toLocaleString()}
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--brand-coral)', fontWeight: 800 }}>
                        ${row.total_monthly_waste?.toFixed(2) ?? '0.00'}/mo
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--ink-600)' }}>
                        ${row.total_annual_waste?.toFixed(2) ?? '0.00'}/yr
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--ink-800)' }}>
                        ${row.unattached_ebs_waste?.toFixed(2) ?? '0.00'}
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--ink-800)' }}>
                        ${row.idle_nat_gateway_waste?.toFixed(2) ?? '0.00'}
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--ink-800)' }}>
                        ${row.idle_eip_waste?.toFixed(2) ?? '0.00'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#059669', fontWeight: 700 }}>
                        ${row.graviton_potential_savings?.toFixed(2) ?? '0.00'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: 'var(--canvas-subtle)',
                          border: '1px solid var(--border-subtle)',
                          fontSize: 11,
                          fontWeight: 700,
                          color: 'var(--ink-700)',
                          fontFamily: 'var(--font-sans)'
                        }}>
                          {row.findings_count} items
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
